import { NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  FilterOptionComponent,
  FilterOptionsPricingTire,
  PaginationConfig,
  PricingTierActions,
  PricingTierPayload,
  PricingTierStatus,
  selectPricingTier,
  selectPricingTierCRUDStatus,
} from '@shared';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { firstValueFrom, Observable } from 'rxjs';
import { ModalService, PaginationService, PricingTier, ToastService, UtilsService } from 'thkee-common';
import { CommonPaginationComponent } from '../../../@shared/components/common-pagination/common-pagination.component';
import { DataTableComponent } from '../../../@shared/components/data-table/data-table.component';
import { FilterOptionComponent as FilterOptionComponent_1 } from '../../../@shared/components/filter-option/filter-option.component';
const pageSize: number = 10;
@Component({
  selector: 'app-price-tiers',
  templateUrl: './price-tiers.component.html',
  styleUrls: ['./price-tiers.component.scss'],
  imports: [
    RouterLink,
    AngularSvgIconModule,
    FilterOptionComponent_1,
    DataTableComponent,
    NgTemplateOutlet,
    CommonPaginationComponent,
    ReactiveFormsModule,
    FormsModule,
  ],
})
export class PriceTiersComponent implements OnInit {
  private store = inject(Store);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private router = inject(Router);
  private paginationService = inject(PaginationService);
  private utilsService = inject(UtilsService);
  private activatedRoute = inject(ActivatedRoute);

  title: string = 'Pricing Tiers';
  pricingTierList$: Observable<any> = this.store.select(selectPricingTier);
  priceTierData: Partial<PricingTier>[] = [];

  pricingTierList: any = [];
  isLoading: boolean = true;

  status$: Observable<PricingTierStatus> = this.store.select(selectPricingTierCRUDStatus);

  queryParams: PricingTierPayload = { page: 1, page_size: pageSize };
  filterValues: FilterOptionsPricingTire = {
    price_tier_status: [],
  };
  filterOptions: FilterOptionsPricingTire = {
    price_tier_status: [],
  };
  isDropdown: boolean[] = [];

  ngOnInit(): void {
    this.setFilterOptions();
    this.queryUpdater();
    this.store.dispatch(PricingTierActions.clearPricingTierStatus());

    this.pricingTierList$.subscribe((item) => {
      this.paginationService.getPager(item?.pricingTierList?.count, this.queryParams.page, this.queryParams.page_size);
      if (typeof item.pricingTierList.results !== 'undefined') {
        this.pricingTierList = item?.pricingTierList?.results || [];
      }
      this.isLoading = false;
    });
  }

  private queryUpdater() {
    firstValueFrom(this.activatedRoute.queryParams).then((params) => {
      this.queryParams.page = params['page'] ? +params['page'] : 1;
      this.queryParams.page_size = params['page_size'] ? +params['page_size'] : 10;
      this.store.dispatch(PricingTierActions.loadPricingTierList(this.queryParams));
    });
  }

  remove(data: PricingTier) {
    this.store.dispatch(PricingTierActions.removePricingTier({ id: data?.id }));

    this.status$.subscribe((status) => {
      if (status === PricingTierStatus.Success) {
        this.toastService.message({
          message: `Pricing Tier removed successfully`,
        });
        this.router.navigate(['/price-management/tiers']);
      }
    });
  }

  deleteConfirmation(data: PricingTier) {
    this.modalService.confirm({
      title: 'Delete Confirmation',
      message: `Are you sure?`,
      onConfirm: () => {
        // Con confirm action here
        // console.log('Delete this: ', { id: data?.id });
        this.remove(data);
      },
    });
  }

  // Dropdown
  dropdown(type: number) {
    this.isDropdown[type] = !this.isDropdown[type];
  }

  paginat(page_number: number) {
    this.queryParams = Object.assign({}, this.queryParams);
    this.queryParams.page = page_number;
    this.store.dispatch(PricingTierActions.loadPricingTierList(this.queryParams));
  }

  paginationConfig: PaginationConfig = {
    pageCountSummary: true,
    pageSize: true,
    firstAndLast: true,
  };

  pageSizeAction(value: number) {
    this.isLoading = true;
    this.queryParams.page_size = value;
    this.queryParams.page = 1;
    this.store.dispatch(PricingTierActions.loadPricingTierList(this.queryParams));
  }

  paginationAction(page_number: number) {
    this.isLoading = true;
    this.queryParams.page = page_number;
    this.store.dispatch(PricingTierActions.loadPricingTierList(this.queryParams));
  }

  setFilterOptions() {
    this.filterOptions.price_tier_status = [
      { value: 'true', label: $localize`Active` },
      { value: 'false', label: $localize`Inactive` },
    ];
  }

  filterHandler(event: { action: string; data: {} }) {
    if (event.action === 'change') {
      const queryParams = this.utilsService.convertQueryParams(event.data);
      this.queryParams = { ...this.queryParams, ...queryParams };
    }
    if (event.action === 'change-input') {
      this.queryParams = { ...this.queryParams, ...event.data };
    }
    if (event.action === 'reset') {
      this.isDropdown[1] = false;
      this.clearFilter([]);
      this.queryParams = { page: 1, page_size: pageSize };
      this.store.dispatch(PricingTierActions.loadPricingTierList(this.queryParams));
    }
  }

  clearFilter(filters: FilterOptionComponent[]) {
    if (filters) {
      filters.forEach((filter: FilterOptionComponent) => {
        filter.reset();
      });
    }
  }

  submitFilter(filters: FilterOptionComponent[]) {
    this.isDropdown[1] = false;
    this.isLoading = true;
    this.store.dispatch(PricingTierActions.loadPricingTierList(this.queryParams));
  }

  search: string = '';
  searchEvent(event: Event) {
    this.isLoading = true;
    const target = event.target as HTMLInputElement;
    this.queryParams.currency_code = target.value;
    this.queryParams.page = 1;
    this.store.dispatch(PricingTierActions.loadPricingTierList(this.queryParams));
  }
}
